import type { CommercialDashboardResponse, KpiValue } from '@troia-v3/types';

import { formatBRL, formatDeltaPct, formatNumber, formatPct } from '@/shared/components/ui';
import type { IconName } from '@/shared/icons';

/**
 * Catálogo de widgets (blocos de indicador) do dashboard comercial.
 *
 * A ORDEM deste array é a ordem de exibição: o usuário escolhe QUAIS aparecem,
 * a posição vem daqui. Por isso os padrões de hoje (4 de resumo, 4 de
 * atividade) saem exatamente como antes.
 */
export const COMMERCIAL_WIDGET_IDS = [
  'revenue',
  'deals',
  'avgTicket',
  'conversionRate',
  'leads',
  'capturedLeads',
  'capturedWon',
  'capturedConversionRate',
  'capturedRevenue',
  'visits',
  'visitConversionRate',
  'calls',
  'emails',
  'messages',
  'meetings',
] as const;

export type CommercialWidgetId = (typeof COMMERCIAL_WIDGET_IDS)[number];

export const DEFAULT_SUMMARY_WIDGETS: CommercialWidgetId[] = [
  'revenue',
  'deals',
  'avgTicket',
  'conversionRate',
];

export const DEFAULT_ACTIVITY_WIDGETS: CommercialWidgetId[] = [
  'calls',
  'emails',
  'messages',
  'meetings',
];

export type CommercialWidgetGroup = 'Vendas' | 'Captura' | 'Visitas' | 'Atividades';

export interface WidgetTileContent {
  value: string;
  deltaPct: number;
  trend?: KpiValue['trend'];
  caption: string;
  extra?: string;
}

export interface CommercialWidgetDef {
  id: CommercialWidgetId;
  label: string;
  icon: IconName;
  group: CommercialWidgetGroup;
  /** `null` = o payload não trouxe o dado (resposta anterior ao campo). */
  build: (data: CommercialDashboardResponse) => WidgetTileContent | null;
}

type KpiKey = keyof CommercialDashboardResponse['kpis'];

/**
 * KPI do payload, ou `undefined`. O tipo diz que todos existem, mas no deploy
 * a tela nova pode receber por instantes a resposta do servidor antigo — sem
 * os campos novos. Melhor um bloco a menos do que "NaN" na tela.
 */
const kpiOf = (data: CommercialDashboardResponse, key: KpiKey): KpiValue | undefined =>
  data.kpis[key] as KpiValue | undefined;

/** "12 de 340 leads" — a conversão também em números, não só em %. */
const ofCaption = (part: KpiValue | undefined, whole: KpiValue | undefined, noun: string): string =>
  part && whole ? `${formatNumber(part.value)} de ${formatNumber(whole.value)} ${noun}` : noun;

const fromKpi = (
  data: CommercialDashboardResponse,
  key: KpiKey,
  format: (v: number) => string,
  caption: string,
): WidgetTileContent | null => {
  const kpi = kpiOf(data, key);
  if (!kpi) return null;
  return { value: format(kpi.value), deltaPct: kpi.deltaPct, trend: kpi.trend, caption };
};

const fromActivity = (
  data: CommercialDashboardResponse,
  key: 'calls' | 'emails' | 'messages',
): WidgetTileContent => {
  const counter = data.teamActivities[key];
  return { value: formatNumber(counter.value), deltaPct: counter.deltaPct, caption: 'vs. anterior' };
};

export const COMMERCIAL_WIDGETS: Record<CommercialWidgetId, CommercialWidgetDef> = {
  revenue: {
    id: 'revenue',
    label: 'Receita',
    icon: 'dollar-sign',
    group: 'Vendas',
    build: (d) => fromKpi(d, 'revenue', formatBRL, 'vs. período anterior'),
  },
  deals: {
    id: 'deals',
    label: 'Vendas',
    icon: 'shopping-bag',
    group: 'Vendas',
    build: (d) => fromKpi(d, 'deals', formatNumber, 'negócios ganhos'),
  },
  avgTicket: {
    id: 'avgTicket',
    label: 'Ticket médio',
    icon: 'tag',
    group: 'Vendas',
    build: (d) => fromKpi(d, 'avgTicket', formatBRL, 'por negócio'),
  },
  conversionRate: {
    id: 'conversionRate',
    label: 'Conversão',
    icon: 'percent',
    group: 'Vendas',
    build: (d) =>
      fromKpi(
        d,
        'conversionRate',
        formatPct,
        kpiOf(d, 'leads') ? ofCaption(d.kpis.deals, kpiOf(d, 'leads'), 'leads') : 'ganhos / criados',
      ),
  },
  leads: {
    id: 'leads',
    label: 'Leads',
    icon: 'users',
    group: 'Vendas',
    build: (d) => fromKpi(d, 'leads', formatNumber, 'criados no período'),
  },
  capturedLeads: {
    id: 'capturedLeads',
    label: 'Leads captados',
    icon: 'qr-code',
    group: 'Captura',
    build: (d) => fromKpi(d, 'capturedLeads', formatNumber, 'pelo formulário de captura'),
  },
  capturedWon: {
    id: 'capturedWon',
    label: 'Vendas via captura',
    icon: 'check-circle',
    group: 'Captura',
    build: (d) => fromKpi(d, 'capturedWon', formatNumber, 'dos captados no período'),
  },
  capturedConversionRate: {
    id: 'capturedConversionRate',
    label: 'Conversão via captura',
    icon: 'target',
    group: 'Captura',
    build: (d) =>
      fromKpi(
        d,
        'capturedConversionRate',
        formatPct,
        ofCaption(kpiOf(d, 'capturedWon'), kpiOf(d, 'capturedLeads'), 'captados'),
      ),
  },
  capturedRevenue: {
    id: 'capturedRevenue',
    label: 'Receita via captura',
    icon: 'award',
    group: 'Captura',
    build: (d) => fromKpi(d, 'capturedRevenue', formatBRL, 'dos captados ganhos'),
  },
  visits: {
    id: 'visits',
    label: 'Visitas',
    icon: 'eye',
    group: 'Visitas',
    build: (d) => fromKpi(d, 'visits', formatNumber, 'medidas no período'),
  },
  visitConversionRate: {
    id: 'visitConversionRate',
    label: 'Conversão de visitas',
    icon: 'trending-up',
    group: 'Visitas',
    build: (d) =>
      fromKpi(
        d,
        'visitConversionRate',
        formatPct,
        ofCaption(d.kpis.deals, kpiOf(d, 'visits'), 'visitas'),
      ),
  },
  calls: {
    id: 'calls',
    label: 'Ligações',
    icon: 'phone',
    group: 'Atividades',
    build: (d) => fromActivity(d, 'calls'),
  },
  emails: {
    id: 'emails',
    label: 'Emails',
    icon: 'mail',
    group: 'Atividades',
    build: (d) => fromActivity(d, 'emails'),
  },
  messages: {
    id: 'messages',
    label: 'Mensagens',
    icon: 'message-square',
    group: 'Atividades',
    build: (d) => fromActivity(d, 'messages'),
  },
  meetings: {
    id: 'meetings',
    label: 'Reuniões realizadas',
    icon: 'calendar',
    group: 'Atividades',
    build: (d) => {
      const m = d.teamActivities.meetings;
      return {
        value: formatNumber(m.value),
        deltaPct: m.deltaPct,
        caption: 'vs. anterior',
        ...(m.noShowPct > 0 ? { extra: `no-show ${formatDeltaPct(m.noShowPct)}` } : {}),
      };
    },
  },
};

export const isCommercialWidgetId = (v: unknown): v is CommercialWidgetId =>
  typeof v === 'string' && (COMMERCIAL_WIDGET_IDS as readonly string[]).includes(v);
